// Horizontal-scrolling picker used by Step 2 for products and media
// (and the cross-direction "related" sub-ribbons surfaced when an
// item is selected). Generic on the row shape — caller provides:
//
//   items:      array of rows
//   getId:      (row) => unique key
//   render:     (row, selected, onClick) => ReactNode for one tile
//   loading:    optional spinner state
//   emptyHint:  optional empty-state text
//
// Selection lives in the parent — RibbonPicker is presentational.

import { Box, HStack, Text, Spinner, Flex, Tooltip } from '@chakra-ui/react';
import type { ReactNode } from 'react';

// Per-seed concept-usage state, fetched from /api/seeds/usage and
// surfaced as a dot row + ad-count badge on each tile so the operator
// can decide whether this seed has fresh angles left to render against.
export type SeedUsage = {
  adCount:           number;
  conceptsTotal:     number;
  conceptsUsed:      number;
  conceptsRemaining: { id: string; name: string }[];
};

// Compact tile overlay — top-left ad-count badge + bottom-left dot row.
// Top-right is reserved for the selection checkmark; bottom-left dots
// stay clear of the bottom-right where MediaTile shows the VIDEO badge.
// Hover surfaces the full breakdown via a Chakra Tooltip.
export function SeedUsageOverlay({ usage }: { usage: SeedUsage }) {
  const { adCount, conceptsTotal, conceptsUsed, conceptsRemaining } = usage;
  const isFresh     = adCount === 0;
  const isExhausted = conceptsTotal > 0 && conceptsUsed >= conceptsTotal;
  // Show a placeholder row of 4 (the Director's default N) when no
  // Director has fired yet so every tile has a visible indicator —
  // operator can SEE the affordance exists and learn what it means
  // before they've run their first generation.
  const displayedTotal = conceptsTotal > 0 ? conceptsTotal : 4;

  const tooltipLabel = isFresh
    ? `Fresh seed — no ads yet. The Director emits ${displayedTotal} concepts on first run; each dot fills as a concept gets rendered against this seed.`
    : `${adCount} ad${adCount === 1 ? '' : 's'} rendered · ${conceptsUsed} of ${conceptsTotal} directions used`
      + (conceptsRemaining.length
          ? `\nFresh angles: ${conceptsRemaining.slice(0, 4).map(c => c.name).join(', ')}`
          : conceptsTotal > 0 ? '\nAll directions explored — re-runs rotate via runId' : '');

  // Tooltip needs a single interactive child. Use a flexed bottom
  // strip with pointer-events:auto so hover lands on the badge + dots
  // without blocking the tile's click target.
  return (
    <Tooltip label={tooltipLabel} placement="top" hasArrow whiteSpace="pre-line" fontSize="11px">
      <Box
        position="absolute" left={0} right={0} bottom={0}
        display="flex" alignItems="flex-end" justifyContent="space-between"
        p={1.5}
        pointerEvents="auto"
      >
        <HStack
          spacing={0.5}
          bg="blackAlpha.800"
          px={1.5} py={1}
          borderRadius="md"
        >
          {displayedTotal <= 6 ? (
            Array.from({ length: displayedTotal }).map((_, i) => (
              <Box
                key={i}
                w="7px" h="7px"
                borderRadius="full"
                bg={i < conceptsUsed ? (isExhausted ? 'green.300' : 'rsViolet.300') : 'transparent'}
                borderWidth={i < conceptsUsed ? 0 : '1.5px'}
                borderColor="whiteAlpha.800"
              />
            ))
          ) : (
            <Text fontSize="9px" color="white" fontWeight="800" letterSpacing="0.04em">
              {conceptsUsed}/{conceptsTotal}
            </Text>
          )}
        </HStack>
        <Box
          bg={isFresh ? 'rsViolet.500' : isExhausted ? 'green.600' : 'blackAlpha.800'}
          color="white"
          px={1.5} py={0.5}
          borderRadius="md"
          fontSize="9px" fontWeight="800"
          letterSpacing="0.04em"
        >
          {isFresh ? 'NEW' : isExhausted ? `ALL ${adCount}` : `${adCount} AD${adCount === 1 ? '' : 'S'}`}
        </Box>
      </Box>
    </Tooltip>
  );
}

type Props<T> = {
  items:        T[];
  getId:        (row: T) => string;
  render:       (row: T, selected: boolean, onClick: () => void) => ReactNode;
  selectedIds:  Set<string>;
  onToggle:     (id: string) => void;
  loading?:     boolean;
  emptyHint?:   string;
};

export function RibbonPicker<T>({
  items, getId, render, selectedIds, onToggle, loading, emptyHint
}: Props<T>) {
  if (loading && items.length === 0) {
    return (
      <Flex h="120px" align="center" justify="center" bg="gray.50" borderRadius="md">
        <Spinner size="sm" />
      </Flex>
    );
  }
  if (items.length === 0) {
    return (
      <Flex h="120px" align="center" justify="center" bg="gray.50" borderRadius="md">
        <Text fontSize="xs" color="brand.muted">{emptyHint || 'Nothing here yet.'}</Text>
      </Flex>
    );
  }
  return (
    <Box
      overflowX="auto"
      overflowY="hidden"
      pb={2}
      sx={{
        // Tighten the scrollbar so it doesn't dominate the strip
        '&::-webkit-scrollbar':       { height: '8px' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' }
      }}
    >
      <HStack spacing={3} align="stretch" minW="min-content">
        {items.map(row => {
          const id = getId(row);
          const selected = selectedIds.has(id);
          return (
            <Box key={id} flexShrink={0}>
              {render(row, selected, () => onToggle(id))}
            </Box>
          );
        })}
      </HStack>
    </Box>
  );
}

// Standard tile shapes — caller can opt out and roll their own render
// fn, but these match the visual language used across the wizard.

// Human-readable copy for the two disjoint reasons a product has no usable
// generation seed (services/catalogImageQuality.js on the backend — keep
// these two reasons in sync with that module's `unusableSeedImageReason`).
const SEED_ISSUE_COPY: Record<string, { badge: string; tooltip: string }> = {
  missing: {
    badge:   'NO PHOTO',
    tooltip: 'No product photo was ever synced for this SKU — there is nothing to generate ads from yet. Sync the catalog again or upload a photo before selecting this product.'
  },
  'thumbnail-only': {
    badge:   'BROKEN IMAGE',
    tooltip: "This product's only image is a low-resolution Google Shopping thumbnail, not a real product photo — it won't load and can't seed a generation. Sync a real image (e.g. from the merchant's own site) before selecting this product."
  }
};

// 2026-08-19 — a THIRD state, distinct from both entries above: the seed
// photo is fine (seedUnusable is false), it just hasn't been mirrored into
// a generation-ready Media doc yet (services/catalogImageQuality.js's
// `pickerBlockReason === 'materializing'`, catalogMaterializeDrainService.js
// on the backend). Unlike a dead seed this is NOT permanent and the card
// must stay selectable — clicking it already triggers the existing
// per-product on-demand backfill (GET /api/catalog/:id), which typically
// lands in a couple of seconds. Deliberately a non-red, non-disabling
// badge so it reads as "hang on" rather than "broken forever".
const PREPARING_BADGE = { badge: 'PREPARING', tooltip: "This product's photo hasn't been prepared for ad generation yet. Selecting it starts preparing it (usually a few seconds); use \"Prepare catalog images\" above to get the whole brand ready at once instead of one card at a time." };

export function ProductTile({
  product, selected, onClick, usage
}: {
  product: {
    id: string; title: string; imageUrl: string | null;
    price?: number | null; currency?: string | null; brand?: string | null;
    // 2026-08-18 — surfaced by routes/catalog.js so the picker can refuse a
    // dead seed instead of silently rendering it as a normal, selectable
    // card. seedIssue is 'missing' | 'thumbnail-only' | null | undefined.
    seedUnusable?: boolean; seedIssue?: string | null;
    // 2026-08-19 — same vocabulary, extended: null once the row is both
    // seed-safe AND materialized (imageMediaId set); 'materializing' when
    // the seed is fine but not yet materialized; otherwise mirrors
    // seedIssue. See catalogImageQuality.js's `catalogSeedFields`.
    pickerBlockReason?: string | null;
  };
  selected: boolean;
  onClick: () => void;
  usage?: SeedUsage;
}) {
  const unusable = !!product.seedUnusable;
  const issue = product.seedIssue ? SEED_ISSUE_COPY[product.seedIssue] : null;
  const preparing = !unusable && product.pickerBlockReason === 'materializing';

  const tile = (
    <Box
      as="button"
      type="button"
      onClick={() => { if (!unusable) onClick(); }}
      w="160px"
      bg="brand.surface"
      borderWidth="2px"
      borderColor={selected ? 'rsViolet.400' : 'brand.border'}
      borderRadius="md"
      overflow="hidden"
      textAlign="left"
      transition="all 120ms"
      cursor={unusable ? 'not-allowed' : 'pointer'}
      opacity={unusable ? 0.55 : 1}
      disabled={unusable || undefined}
      aria-disabled={unusable || undefined}
      aria-pressed={selected}
      _hover={unusable ? undefined : { borderColor: selected ? 'rsViolet.400' : 'gray.300' }}
      boxShadow={selected ? '0 0 0 3px rgba(122,53,232,0.18)' : undefined}
    >
      <Box w="100%" h="120px" bg="gray.100" position="relative">
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: unusable ? 'grayscale(1)' : undefined }}
            loading="lazy"
          />
        )}
        {unusable && issue && (
          <Box
            position="absolute" top={1.5} left={1.5}
            bg="red.600" color="white"
            px={1.5} py={0.5} borderRadius="md"
            fontSize="9px" fontWeight="800" letterSpacing="0.04em"
          >
            {issue.badge}
          </Box>
        )}
        {preparing && (
          <Box
            position="absolute" top={1.5} left={1.5}
            bg="blue.600" color="white"
            px={1.5} py={0.5} borderRadius="md"
            fontSize="9px" fontWeight="800" letterSpacing="0.04em"
          >
            {PREPARING_BADGE.badge}
          </Box>
        )}
        {selected && !unusable && (
          <Box
            position="absolute" top={1.5} right={1.5}
            bg="rsViolet.500" color="white"
            w="20px" h="20px" borderRadius="full"
            display="flex" alignItems="center" justifyContent="center"
            fontSize="11px" fontWeight="800"
          >
            ✓
          </Box>
        )}
        {usage && !unusable && <SeedUsageOverlay usage={usage} />}
      </Box>
      <Box p={2}>
        <Text fontSize="11px" fontWeight="700" color="brand.ink" noOfLines={2}>{product.title}</Text>
        {product.price != null && (
          <Text fontSize="10px" color="brand.muted" mt={0.5}>
            {product.currency || ''} {Number(product.price).toFixed(2)}
          </Text>
        )}
      </Box>
    </Box>
  );

  // Make WHY unavailable (or still-preparing) discoverable — an operator
  // needs the reason, not just a greyed-out card with no explanation (that
  // was the whole bug).
  const tooltipLabel = issue ? issue.tooltip : preparing ? PREPARING_BADGE.tooltip : null;
  return tooltipLabel ? (
    <Tooltip label={tooltipLabel} placement="top" hasArrow fontSize="11px" maxW="220px">
      {tile}
    </Tooltip>
  ) : tile;
}

export function MediaTile({
  media, selected, onClick, usage
}: {
  media: { id: string; fileType: 'image' | 'video'; fileUrl: string; creatorHandle?: string | null; primarySubjectLabel?: string | null };
  selected: boolean;
  onClick: () => void;
  usage?: SeedUsage;
}) {
  // Video sources need a Cloudinary first-frame transform — same trick
  // the media-library Sidebar uses. Falls through to the raw fileUrl
  // for non-Cloudinary or image media.
  const isVideo = media.fileType === 'video';
  const thumbSrc = isVideo
    ? (media.fileUrl.includes('/video/upload/')
        ? media.fileUrl.replace('/video/upload/', '/video/upload/so_0/').replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, '.jpg$2')
        : media.fileUrl)
    : media.fileUrl;
  const label = media.primarySubjectLabel || media.creatorHandle || (isVideo ? 'Video' : 'Image');
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      w="120px"
      bg="brand.surface"
      borderWidth="2px"
      borderColor={selected ? 'rsViolet.400' : 'brand.border'}
      borderRadius="md"
      overflow="hidden"
      textAlign="left"
      transition="all 120ms"
      aria-pressed={selected}
      _hover={{ borderColor: selected ? 'rsViolet.400' : 'gray.300' }}
      boxShadow={selected ? '0 0 0 3px rgba(122,53,232,0.18)' : undefined}
    >
      <Box w="100%" h="150px" bg="gray.100" position="relative">
        {thumbSrc && (
          <img
            src={thumbSrc}
            alt={label}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            loading="lazy"
          />
        )}
        {isVideo && (
          <Box
            position="absolute" bottom={1} left={1}
            bg="blackAlpha.700" color="white"
            px={1.5} py={0.5} borderRadius="md"
            fontSize="9px" fontWeight="800"
          >
            VIDEO
          </Box>
        )}
        {selected && (
          <Box
            position="absolute" top={1.5} right={1.5}
            bg="rsViolet.500" color="white"
            w="20px" h="20px" borderRadius="full"
            display="flex" alignItems="center" justifyContent="center"
            fontSize="11px" fontWeight="800"
          >
            ✓
          </Box>
        )}
        {usage && <SeedUsageOverlay usage={usage} />}
      </Box>
      <Box p={1.5}>
        <Text fontSize="10px" fontWeight="700" color="brand.ink" noOfLines={1}>{label}</Text>
      </Box>
    </Box>
  );
}
